import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/framework-originated/i18n";

// ---------------------------------------------------------------------------
// Pagination — 统一分页工具条(demo/pagination-toolbar.html)。
//
// 受控组件:页面持有 page / pageSize / total,组件只负责渲染与 clamp。
// - loading 时沿用上一次已知 total,避免 refetch 期间 total 瞬时塌成 0 把
//   page 误判越界并打回第 1 页(与 TableShell 的 lastTotalRef 同一处理)。
// - 改每页条数会先回调 onPageSizeChange,再回到第 1 页。
// ---------------------------------------------------------------------------

export interface PaginationProps {
  page: number;
  total?: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** 传入才渲染「每页 N 条」选择器。 */
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** 数据加载中:期间不收敛页码,并沿用上一次 total。 */
  loading?: boolean;
  /** 传入才显示「本页 N 条」。 */
  pageItemCount?: number;
  className?: string;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function pageItems(current: number, total: number): (number | "ellipsis")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const nums = [...new Set([1, total, current, current - 1, current + 1])]
    .filter((n) => n >= 1 && n <= total)
    .sort((a, b) => a - b);
  const out: (number | "ellipsis")[] = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push("ellipsis");
    out.push(n);
  });
  return out;
}

export function Pagination({
  page,
  total,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  loading = false,
  pageItemCount,
  className,
}: PaginationProps) {
  const { t } = useTranslation("common");
  const [jump, setJump] = useState("");

  // refetch 期间 total 不可信,沿用上一次非 loading 的值。
  const lastTotalRef = useRef<number | undefined>(undefined);
  if (!loading && total !== undefined) lastTotalRef.current = total;
  const effectiveTotal = (!loading ? total : undefined) ?? lastTotalRef.current ?? 0;

  const totalPages = Math.max(1, Math.ceil(effectiveTotal / pageSize));
  const current = clamp(page, 1, totalPages);

  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  useEffect(() => {
    if (!loading && total !== undefined && page > totalPages) onPageChangeRef.current(totalPages);
  }, [loading, page, total, totalPages]);

  const go = (next: number) => {
    onPageChange(clamp(Math.round(next), 1, totalPages));
  };

  const commitJump = () => {
    const n = Number(jump);
    if (jump.trim() && Number.isFinite(n)) go(n);
    setJump("");
  };

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 border-t border-[#e4eaf1] pt-3 text-xs text-muted-foreground",
        className,
      )}
    >
      <span>
        {t("pagination.totalItems", { total: effectiveTotal })}
        {" · "}
        {t("pagination.pageOf", { page: current, pages: totalPages })}
        {typeof pageItemCount === "number"
          ? ` · ${t("pagination.pageItem", { itemCount: pageItemCount })}`
          : null}
      </span>

      <div className="flex flex-wrap items-center gap-2">
        {onPageSizeChange && (
          <label className="flex items-center gap-1">
            {t("pagination.itemsPerPage")}
            <select
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
                onPageChange(1);
              }}
              className="h-8 rounded-md border border-[#e4eaf1] bg-background px-2 text-xs"
            >
              {[...new Set([...pageSizeOptions, pageSize])].sort((a, b) => a - b).map((size) => (
                <option key={size} value={size}>
                  {t("pagination.sizeOption", { size })}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="flex items-center gap-1">
          <PagerButton
            label={t("pagination.prev")}
            disabled={current <= 1}
            onClick={() => go(current - 1)}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </PagerButton>
          {pageItems(current, totalPages).map((item, i) =>
            item === "ellipsis" ? (
              <span key={`e${i}`} className="min-w-3 text-center">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                aria-current={item === current ? "page" : undefined}
                onClick={() => go(item)}
                className={cn(
                  "inline-flex h-7 min-w-7 items-center justify-center rounded-md border px-1.5 transition-colors",
                  item === current
                    ? "border-primary bg-primary font-semibold text-primary-foreground"
                    : "border-[#e4eaf1] bg-background hover:bg-muted/40",
                )}
              >
                {item}
              </button>
            ),
          )}
          <PagerButton
            label={t("pagination.next")}
            disabled={current >= totalPages}
            onClick={() => go(current + 1)}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </PagerButton>
        </div>

        <div className="flex items-center gap-1">
          <span>{t("pagination.jumpTo")}</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={jump}
            onChange={(e) => setJump(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitJump();
            }}
            aria-label={t("pagination.jumpTo")}
            className="h-8 w-14 rounded-md border border-[#e4eaf1] bg-background px-2 text-center text-xs"
          />
          <span>{t("pagination.jumpUnit")}</span>
          <button
            type="button"
            onClick={commitJump}
            className="h-8 rounded-md border border-[#e4eaf1] bg-background px-2 transition-colors hover:bg-muted/40"
          >
            {t("pagination.go")}
          </button>
        </div>
      </div>
    </div>
  );
}

function PagerButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#e4eaf1] bg-background transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
